import type { Db } from "./client";
import { throwIfPgError } from "./errors";
import type {
  SearchObservationsRow,
  SearchPlantNotesRow,
} from "@garden/types/supabase";

/** Eight kinds, mirrors `observationKindSchema` in `db/schemas.ts`. The
 *  global /observations route uses these for the filter chips. */
export const OBSERVATION_KINDS = [
  "general",
  "yield",
  "health",
  "flowering",
  "pest",
  "weather",
  "move",
  "rotation_note",
] as const;

export type ObservationKind = (typeof OBSERVATION_KINDS)[number];

const DEFAULT_LIMIT = 50;

/** FTS over `plant_observation.body` via the `search_observations` RPC.
 *  Empty `q` returns the most recent observations (RLS still applies, so
 *  this only ever surfaces the current user's rows). */
export async function searchObservations(
  client: Db,
  q: string,
  opts?: { kind?: ObservationKind | null; limit?: number },
): Promise<SearchObservationsRow[]> {
  const { data, error } = await client.rpc("search_observations", {
    q,
    kind_filter: opts?.kind ?? null,
    result_limit: opts?.limit ?? DEFAULT_LIMIT,
  });
  throwIfPgError(error);
  return (data ?? []) as SearchObservationsRow[];
}

/** FTS over `plant.notes` via the `search_plant_notes` RPC. */
export async function searchPlantNotes(
  client: Db,
  q: string,
  opts?: { limit?: number },
): Promise<SearchPlantNotesRow[]> {
  const { data, error } = await client.rpc("search_plant_notes", {
    q,
    result_limit: opts?.limit ?? DEFAULT_LIMIT,
  });
  throwIfPgError(error);
  return (data ?? []) as SearchPlantNotesRow[];
}
